import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Calendar,
    Search,
    Newspaper,
    ArrowRight,
    AlertCircle,
} from "lucide-react";
import { currentAffairsApi } from "../../../../api/api";
import api from "../../../../utils/api";

export default function CurrentAffairsDaily() {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentAffairs, setCurrentAffairs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`${currentAffairsApi}`)
            .then((response) => {
                // Ensure response is an array
                if (Array.isArray(response.data)) {
                    setCurrentAffairs(response.data);
                } else if (
                    response.data?.data &&
                    Array.isArray(response.data.data)
                ) {
                    setCurrentAffairs(response.data.data);
                } else {
                    console.error(
                        "Invalid API response format:",
                        response.data,
                    );
                    setCurrentAffairs([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching current affairs:", error);
                setCurrentAffairs([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // Filtering logic
    const filteredUpdates = useMemo(() => {
        return currentAffairs
            .filter((item) => item.status === true || item.status === 1)
            .filter(
                (item) =>
                    item.title
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    item.short_description
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()),
            )
            .sort(
                (a, b) =>
                    new Date(b.created_at || 0) - new Date(a.created_at || 0),
            );
    }, [currentAffairs, searchTerm]);

    // Animation variants matching established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full border-t border-gray-200 min-h-screen flex flex-col">
            <div className="max-w-7xl mx-auto w-full">
                {/* Standardized Editorial Header */}
                <div className="mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="max-w-3xl mx-auto text-center"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                            Current{" "}
                            <span className="text-yellow-500">Affairs.</span>
                        </h2>
                        <p className="text-gray-500 text-lg font-medium leading-relaxed">
                            Stay updated with concise, trustworthy summaries
                            tailored specifically for your government exam
                            preparation.
                        </p>
                    </motion.div>
                </div>

                {/* Standardized Search Bar */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mb-12 relative max-w-2xl mx-auto"
                >
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Search className="text-gray-400" size={20} />
                    </div>
                    <input
                        type="search"
                        placeholder="Search topics, events, or keywords..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-white border border-gray-200 text-neutral-900 text-base py-4 pl-12 pr-6 rounded-xl shadow-sm focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all font-medium placeholder:text-gray-400"
                        aria-label="Search current affairs"
                    />
                </motion.div>

                {/* Content Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm animate-pulse h-64 p-6 flex flex-col"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg"></div>
                                    <div className="w-24 h-6 bg-gray-100 rounded"></div>
                                </div>
                                <div className="w-3/4 h-6 bg-gray-100 rounded mb-4"></div>
                                <div className="w-full h-4 bg-gray-100 rounded mb-2"></div>
                                <div className="w-5/6 h-4 bg-gray-100 rounded mb-6"></div>
                                <div className="mt-auto w-1/3 h-4 bg-gray-100 rounded"></div>
                            </div>
                        ))}
                    </div>
                ) : filteredUpdates.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center max-w-2xl mx-auto"
                    >
                        <div className="w-16 h-16 bg-gray-50 border border-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-900 mb-2">
                            No updates found
                        </h3>
                        <p className="text-gray-500 font-medium text-sm">
                            We couldn't find any current affairs matching "
                            {searchTerm}". Try a different keyword or clear your
                            search.
                        </p>
                    </motion.div>
                ) : (
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <AnimatePresence>
                            {filteredUpdates.map((currentAffair) => (
                                <motion.div
                                    variants={itemVariants}
                                    key={currentAffair.id}
                                    layout
                                    className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 flex flex-col overflow-hidden"
                                >
                                    {/* Hover Accent Line */}
                                    <div className="absolute top-0 left-0 w-full h-[3px] bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 ease-out z-10"></div>

                                    <Link
                                        to={`/current-affairs-details/${currentAffair.id}`}
                                        className="p-6 flex flex-col flex-grow focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-inset rounded-xl"
                                    >
                                        {/* Card Header: Icon & Date */}
                                        <div className="flex justify-between items-start mb-5">
                                            <div className="w-12 h-12 bg-gray-50 group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-colors duration-300 border border-gray-100 group-hover:border-yellow-400">
                                                <Newspaper size={20} />
                                            </div>
                                            <div className="flex items-center px-2.5 py-1 bg-gray-50 border border-gray-100 text-gray-500 rounded font-bold text-[10px] uppercase tracking-widest group-hover:border-gray-200 transition-colors">
                                                <Calendar
                                                    size={12}
                                                    className="mr-1.5 text-red-700"
                                                />
                                                <time
                                                    dateTime={
                                                        currentAffair.created_at
                                                    }
                                                >
                                                    {currentAffair.created_at
                                                        ? new Intl.DateTimeFormat(
                                                              "en-IN",
                                                              {
                                                                  day: "2-digit",
                                                                  month: "short",
                                                                  year: "numeric",
                                                              },
                                                          ).format(
                                                              new Date(
                                                                  currentAffair.created_at,
                                                              ),
                                                          )
                                                        : "N/A"}
                                                </time>
                                            </div>
                                        </div>

                                        <h3 className="text-xl font-black text-neutral-900 mb-3 line-clamp-2 group-hover:text-red-700 transition-colors leading-snug">
                                            {currentAffair.title}
                                        </h3>

                                        <p className="text-sm text-gray-500 font-medium leading-relaxed line-clamp-2 mb-6 flex-grow">
                                            {currentAffair.short_description}
                                        </p>

                                        <div className="mt-auto pt-4 border-t border-gray-100 flex items-center text-sm font-bold text-neutral-900 group-hover:text-yellow-600 transition-colors">
                                            Read Full Article
                                            <ArrowRight
                                                size={16}
                                                className="ml-2 transform group-hover:translate-x-1 transition-transform"
                                            />
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
